// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

// EXTERNAL DEPENDENCY
import { get } from 'lodash';

// SERVICES
import { NotificationService } from '../services/notification.service';

// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import { State } from '../../../root-state';
import * as notificationAction from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { NOTIFICATION_NGRX_CONSTANT } from '../../../../constants/ngrx-store/notification';

/**
 * This effect is for listen master notification actions, execute master notification service call and dispatch in master notification reducer.
 * For master notification api call purpose.
 *
 * @author Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 27/04/2020 (Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>)  created master notification effect
 */
@Injectable()
export class NotificationEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private notificationService: NotificationService) { }

  /**
   *  Master Notification Effect
   * @param type :  action name.
   * @param payload : object to identify common master notification method base on module and method.
   * @returns Empty action dispatch to reducer.
   */
  masterNotificationCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<notificationAction.NotificationCommonAction>(notificationAction.ActionTypes.NOTIFICATION_COMMON),
    withLatestFrom(this.store),
    mergeMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common: any = NOTIFICATION_NGRX_CONSTANT[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      if (common.CACHING && !common.INDIVIDUAL && state.notification && state.notification[common.STATE]) {
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.notification[common.STATE] });
        }
        // make empty action call for skip
        return of(new globalActions.EmptyAction({}));
      } else if (common.CACHING && common.INDIVIDUAL && state.notification &&
        state.notification[common.STATE] &&
        state.notification[common.STATE][action.payload.data[common.UNIQUE_KEY]]) {

        // data send from redux store if already available
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.notification[common.STATE][action.payload.data[common.UNIQUE_KEY]] });
        }

        return of(new globalActions.EmptyAction({}));
      }

      // make api call
      return this.notificationService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            let actions: any[] = [];

            // this code added to else condition of success notification
            // if (action.payload.callback) {
            //   action.payload.callback({ success: true, data: get(data, common.API_RESPONSE) });
            // }
            // dispatch master notification success action
            actions = [new notificationAction.NotificationCommonSuccessAction({ data, common })];

            if (common.SUCCESS_NOTIFICATION) {
              // dispatch popup notification for open success popup
              actions.push(new globalActions.PopupNotificationAction({
                actionType: notificationAction.ActionTypes.NOTIFICATION_COMMON, type: ModalType.SUCCESS,
                callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                code: `${common.SUCCESS}`,
              }));
            } else {
              action.payload.callback && action.payload.callback({ success: true, data: get(data, common.API_RESPONSE) });
            }

            return actions;
          }),
          catchError(error => {
            console.error(error);
            error.error = error.error || {};
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: notificationAction.ActionTypes.NOTIFICATION_COMMON, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }));
          }));
    }),
  ));


}
